/**
 * RFC 9457 problem gövdesi (sunucunun tek hata biçimi). `code` kararlı ve makine okunurdur; `detail` sunucunun
 * Türkçe açıklamasıdır; `errors` alan bazlı doğrulama hatalarıdır (anahtar camelCase alan yolu).
 */
export interface ProblemBody {
  readonly type?: string | null;
  readonly title?: string | null;
  readonly status?: number | null;
  readonly detail?: string | null;
  readonly instance?: string | null;
  readonly code?: string | null;
  readonly traceId?: string | null;
  readonly errors?: Readonly<Record<string, readonly string[]>> | null;
  readonly retryAfterSeconds?: number | null;
  readonly remainingAttempts?: number | null;
}

/** Gövdesi okunamayan yanıtın kodu (sunucunun genel kodlarıyla aynı adlar). */
const codeByStatus: Readonly<Record<number, string>> = {
  400: 'validation',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  405: 'method_not_allowed',
  409: 'conflict',
  410: 'gone',
  412: 'precondition_failed',
  413: 'payload_too_large',
  415: 'unsupported_media_type',
  422: 'unprocessable',
  429: 'rate_limited',
};

/** Ağ hatasının kodu: istek sunucuya ulaşmadı (çevrimdışı, DNS, bağlantı kesildi). */
export const NETWORK_CODE = 'network';

/** Problem gövdesinin bilinen alanları; kalanı RFC 9457 uzantısıdır (`violations`, `conflict`, `replaced`…). */
const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'type',
  'title',
  'status',
  'detail',
  'instance',
  'code',
  'traceId',
  'errors',
  'retryAfterSeconds',
  'remainingAttempts',
]);

/**
 * API hatası (08 §11.4): problem gövdesinin tipli hâli. `status` 0 ise istek sunucuya ulaşmamıştır. İleti arayüzde
 * `@ticarishift/i18n` `problemMessage(problem)` ile koddan kurulur; `message` yalnız geliştirici içindir.
 */
export class ApiProblem extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string | undefined;
  readonly detail: string | undefined;
  readonly errors: Readonly<Record<string, readonly string[]>>;
  readonly traceId: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly remainingAttempts: number | undefined;
  /**
   * RFC 9457 uzantıları (S20): gövdedeki bilinen alanlar dışındaki her şey, olduğu gibi (ör. planlama ENGEL'inin
   * `violations` listesi, çakışmanın `conflict` kaydı, hızlı kurulumun `replaced` sayıları). Ekran, beklediği alanı
   * kendi tipine kendisi indirger.
   */
  readonly extensions: Readonly<Record<string, unknown>>;

  constructor(status: number, body: ProblemBody = {}, options?: { cause?: unknown }) {
    super(body.detail ?? body.title ?? `HTTP ${status}`, options);
    this.name = 'ApiProblem';
    this.status = status;
    this.code = body.code ?? (status === 0 ? NETWORK_CODE : (codeByStatus[status] ?? 'internal'));
    this.title = body.title ?? undefined;
    this.detail = body.detail ?? undefined;
    this.errors = body.errors ?? {};
    this.traceId = body.traceId ?? undefined;
    this.retryAfterSeconds = body.retryAfterSeconds ?? undefined;
    this.remainingAttempts = body.remainingAttempts ?? undefined;
    this.extensions = Object.fromEntries(Object.entries(body as Record<string, unknown>).filter(([key]) => !KNOWN_FIELDS.has(key)));
  }

  /**
   * Destek kodu (08 §9.3): izleme kimliğinin ilk sekiz hanesi, "4BF9-2F35". Loglarda `TraceId` önekiyle aranır;
   * kullanıcıya tam kimlik gösterilmez.
   */
  get supportCode(): string | undefined {
    const hex = this.traceId?.replace(/[^0-9a-f]/gi, '').slice(0, 8).toUpperCase();
    return hex && hex.length === 8 ? `${hex.slice(0, 4)}-${hex.slice(4)}` : undefined;
  }

  /** İstek sunucuya ulaşmadı. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** Geçici arıza: ağ yok, hız sınırı ya da sunucu hatası. Oturum bu hatalarda kapanmaz. */
  get isTransient(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  /** Alan bazlı doğrulama hatası var mı (form alanlarına bağlanır). */
  get hasFieldErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }
}

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem;
}

/** Ağ hatasını (fetch'in fırlattığı `TypeError`) API hatasına çevirir. */
export function networkProblem(cause: unknown): ApiProblem {
  return new ApiProblem(0, { code: NETWORK_CODE }, { cause });
}

/** İptal edilen istek (AbortSignal): hata değildir, çağırana olduğu gibi döner. */
export function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

/**
 * Başarısız yanıtı API hatasına çevirir. Gövde JSON değilse (ara katman, proxy) kod HTTP durumundan gelir;
 * `Retry-After` başlığı gövdede bekleme yoksa kullanılır.
 */
export async function readProblem(response: Response): Promise<ApiProblem> {
  let body: ProblemBody = {};
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('json')) {
    try {
      const parsed: unknown = await response.json();
      if (parsed && typeof parsed === 'object') {
        body = parsed as ProblemBody;
      }
    } catch {
      // Bozuk gövde: kod durumdan gelir.
    }
  }

  const retryAfter = response.headers.get('retry-after');
  const retryAfterSeconds =
    body.retryAfterSeconds ?? (retryAfter && /^\d+$/.test(retryAfter.trim()) ? Number(retryAfter.trim()) : undefined);
  return new ApiProblem(response.status, { ...body, retryAfterSeconds });
}
